import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import './OrderDetailsModal.css';
import TauriLoader2 from '../../TauriLoader2/TauriLoader2';
import { getAll, getRecordById } from '../../../localDB';
import OpalDataTable from '../../OpalDataTable/OpalDataTable';
import { formatNumber } from '../../../utils/NumberFormatter';
import appURL from '../../../appURL.json';
import { transformOrder, transformOrderDetails, upsertLocalDataOrderDetails, upsertLocalDataOrders } from '../OrderFactoryData/OrderFactoryData';
import TauriImage from '../../TauriImage/TauriImage';

const OrderDetailsModal = ({ show, onHide, id }) => {
    const [signatureImage, setSignatureImage] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [order, setOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);

    const fetchOrder = async (OrderID = id) => {

        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_GetOrders}/id/${OrderID}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                }
            });

            if (!response.ok) {
                console.error(response);
                return;
            }

            const data = await response.json();
            const transformedOrder = await transformOrder(data.order || null);
            const transformedOrderItems = await transformOrderDetails(data?.details || [], OrderID);

            setSignatureImage(data?.signature);


            await upsertLocalDataOrders(
                transformedOrder ? [transformedOrder] : []
            );
            await upsertLocalDataOrderDetails(transformedOrderItems);

            setOrder(transformedOrder);
            setOrderItems(transformedOrderItems);

        } catch (error) {
            console.log(error);
        }
    };

    const fetchOrderSignature = async (OrderID = id) => {

        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_GetOrders}/id/${OrderID}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                }
            });

            if (!response.ok) {
                console.error(response);
                return;
            }

            const data = await response.json();
            setSignatureImage(data?.signature);

        } catch (error) {
            console.log(error);
        }
    };

    const fetchLocalOrder = async (OrderID = id) => {
        try {

            const order = await getRecordById("Orders", OrderID);
            const _details = await getAll("OrderDetails");

            const details = _details.filter(a => a.OrderID === OrderID)

            setOrder(order);
            setOrderItems(details);

        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (id) {
            try {
                setLoadingData(true);
                fetchOrder(id);
            } catch {
                return
            } finally {
                setLoadingData(false);
            }
        }
    }, [id])

    useEffect(() => {
        if (id) {
            fetchLocalOrder(id);
            fetchOrderSignature(id);
        }
    }, [show])

    const orderDetailColumns = [
        { key: "Item.Description", label: "ITEM" },
        { key: "Unit.Description", label: "UNIT" },
        {
            key: "DetailPrice",
            label: "DETAIL PRICE",
            inputType: "number"
        },
        {
            key: "Quantity",
            label: "Quantity",
            inputType: "number"
        },
        {
            key: "FreeGoods",
            label: "FREE GOODS",
            inputType: "number"
        },
        {
            key: "DiscountPercent",
            label: "DISCOUNT %",
            inputType: "number"
        },
        {
            key: "NetLineTotal",
            label: "NET AMOUNT",
            inputType: "number",
            total: true
        }
    ];

    return (
        <Modal
            className='order-details-modal'
            show={show}
            onHide={onHide}
            size="lg"
            centered
        >
            <Modal.Header>Order Details</Modal.Header>

            <Modal.Body>
                {loadingData ? (
                    <div className="d-flex justify-content-center align-items-center flex-grow-1">
                        <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                    </div>
                ) : (
                    <>
                        {/* Sales Summary */}
                        <div className="sales-summary">
                            <div className="summary-header">ORDER SUMMARY</div>

                            <table className="summary-table">
                                <colgroup>
                                    <col style={{ width: '75%' }} />
                                    <col style={{ width: '25%' }} />
                                </colgroup>
                                <tbody>
                                    <tr>
                                        <td>
                                            <p><span className='summary-label me-1'>ACCOUNT:</span> {order?.Account?.Name ?? '-'}</p>
                                            <p className="inline-wrap">
                                                <span className='summary-label me-1'>DELIVERY ADDRESS:</span>
                                                <span className='summary-value'>
                                                    {order?.DeliveryAddress ?? '-'}
                                                </span>
                                            </p>
                                        </td>
                                        <td>
                                            <p><span className='summary-label me-1'>ORDER DATE:</span> {order?.OrderDate ?? '-'}</p>
                                            <p><span className='summary-label me-1'>ORDER NO.:</span> {order?.OrderNumber ?? '-'}</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <p>
                                                <span className='summary-label me-1'>REMARKS:</span>
                                                <span className='summary-value'>
                                                    {order?.Remarks ?? '-'}
                                                </span>
                                            </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <OpalDataTable
                                data={orderItems}
                                columns={orderDetailColumns}
                            />

                            <div className='summary-totals d-flex align-items-center justify-content-end gap-4 mt-2'>
                                <div className='d-flex gap-1'>
                                    <span className="summary-label">GROSS AMOUNT:</span>
                                    <span className='summary-value'>{formatNumber(order?.GrossTotal)}</span>
                                </div>
                                <div className='d-flex gap-1'>
                                    <span className="summary-label">DISCOUNT AMOUNT:</span>
                                    <span className='summary-value'>{formatNumber(order?.TotalDiscount)}</span>
                                </div>
                                <div className='d-flex gap-1'>
                                    <span className="summary-label">NET AMOUNT:</span>
                                    <span className='summary-value'>{formatNumber(order?.NetTotal)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="sales-order-summary-signature flex-grow-1 d-flex justify-content-center align-items-center">
                            <TauriImage externalImage={signatureImage} />
                        </div>

                        <div className="submit-actions mt-3 d-flex justify-content-end align-items-center gap-3">
                            <Button
                                className="btn-cancel d-flex"
                                onClick={onHide}
                            >
                                <span className="text-nowrap">Close</span>
                            </Button>
                        </div>

                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default OrderDetailsModal;
